import { useEffect, useRef, useState } from 'react';

const VARIANT_CLASS = {
  up: '',
  fade: 'reveal--fade',
  left: 'reveal--left',
  right: 'reveal--right',
  scale: 'reveal--scale',
  soft: 'reveal--soft',
  blur: 'reveal--blur',
};

/**
 * Animates children when they enter or leave the viewport (scroll up & down).
 * @param {'up'|'fade'|'left'|'right'|'scale'|'soft'|'blur'} [variant]
 * @param {number} [delay] - ms before animation starts
 * @param {boolean} [once] - if true, animate only the first time (default false)
 */
function Reveal({
  children,
  className = '',
  variant = 'up',
  delay = 0,
  once = false,
  as: Tag = 'div',
  ...props
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) {
            observer.disconnect();
          }
        } else if (!once) {
          setVisible(false);
        }
      },
      {
        threshold: 0.15,
        /* Trigger a bit early so the motion is fully visible in-frame */
        rootMargin: '0px 0px -6% 0px',
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once]);

  const variantClass = VARIANT_CLASS[variant] ?? '';

  return (
    <Tag
      ref={ref}
      data-reveal=""
      className={`reveal ${variantClass} ${visible ? 'reveal--visible' : ''} ${className}`.trim()}
      style={{ '--reveal-delay': `${delay}ms` }}
      {...props}
    >
      {children}
    </Tag>
  );
}

export default Reveal;
